const API_KEY = process.env.REACT_APP_AWS_FRIENDZ_API_KEY;
const API_BASE_URL = 'https://albvg9jakf.execute-api.us-east-1.amazonaws.com/test';

/**
 * Search for properties for sale by location
 * @param {Object} searchParams - Search parameters
 * @param {string} [searchParams.zipCode] - ZIP code to search
 * @param {string} [searchParams.city] - City name (requires state)
 * @param {string} [searchParams.state] - State code (requires city)
 * @param {number} [searchParams.bedrooms] - Number of bedrooms
 * @param {number} [searchParams.bathrooms] - Number of bathrooms
 * @param {number} [searchParams.minPrice] - Minimum price
 * @param {number} [searchParams.maxPrice] - Maximum price
 * @param {number} [searchParams.limit] - Max results (default 20, max 50)
 * @param {string} [searchParams.userId] - User ID for quota tracking
 * @returns {Promise<Object>} Search results with properties array
 */
export const searchPropertiesByLocation = async (searchParams) => {
  // Build query params, filtering out empty values
  const params = {};

  if (searchParams.zipCode) params.zipCode = searchParams.zipCode;
  if (searchParams.city) params.city = searchParams.city;
  if (searchParams.state) params.state = searchParams.state;
  if (searchParams.bedrooms && searchParams.bedrooms !== 'any') params.bedrooms = searchParams.bedrooms;
  if (searchParams.bathrooms && searchParams.bathrooms !== 'any') params.bathrooms = searchParams.bathrooms;
  if (searchParams.minPrice) params.minPrice = searchParams.minPrice;
  if (searchParams.maxPrice) params.maxPrice = searchParams.maxPrice;
  if (searchParams.propertyType && searchParams.propertyType !== 'any') params.propertyType = searchParams.propertyType;
  if (searchParams.limit) params.limit = searchParams.limit;
  if (searchParams.userId) params.userId = searchParams.userId;

  const queryParams = new URLSearchParams(params).toString();
  const url = `${API_BASE_URL}/properties/search?${queryParams}`;

  const requestOptions = {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': API_KEY
    }
  };

  try {
    const response = await fetch(url, requestOptions);

    if (!response.ok) {
      let errorData;
      try {
        errorData = await response.json();
      } catch {
        const errorText = await response.text();
        errorData = { error: errorText };
      }

      if (response.status === 429) {
        throw new Error(`Monthly limit reached: ${errorData.error || 'Upgrade your plan to continue'}`);
      }

      throw new Error(errorData.error || `Search failed: ${response.status}`);
    }

    const responseData = await response.json();
    return responseData;
  } catch (error) {
    console.error('Error searching properties:', error);
    throw error;
  }
};

/**
 * Format price for display
 * @param {number} price - Price value
 * @returns {string} Formatted price string
 */
export const formatPrice = (price) => {
  if (!price) return 'N/A';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0
  }).format(price);
};

/**
 * Format square footage for display
 * @param {number} sqft - Square footage
 * @returns {string} Formatted square footage string
 */
export const formatSquareFootage = (sqft) => {
  if (!sqft) return 'N/A';
  return new Intl.NumberFormat('en-US').format(sqft) + ' sqft';
};
